/* eslint-disable react-hooks/refs -- false positive: prop forwarding, not ref.current reads */
import {match, Pattern} from 'ts-pattern';

import {KeyboardEvent, useCallback, useEffect, useRef} from 'react';

import {getSafely} from 'shared/web';

export type FocusZoneType =
  | 'relative-dates'
  | 'previous-month-button'
  | 'month-select'
  | 'year-select'
  | 'next-month-button'
  | 'calendar'
  | 'close-button';

export interface FocusTrapConfig {
  enabled: boolean;
  returnFocusOnClose: boolean;
  escapeKeyClosesPopper: boolean;
  tabCycleOrder: FocusZoneType[];
  initialFocusZone?: FocusZoneType;
}

export interface UseDatePickerFocusTrapProps {
  isOpen: boolean;
  onClose: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  popperRef: React.RefObject<HTMLDivElement | null>;
  hasRelativeDates: boolean;
  relativeDatesFocused: boolean;
  focusedRelativeDayIndex: number;
  calendarFocusedDate: Date | null;
  onRelativeDatesFocusChange: (focused: boolean, index?: number) => void;
  onCalendarFocusChange: (date: Date | null) => void;
  defaultCalendarDate?: Date;
  /** When false, opening the popper keeps focus on the input (e.g. click-to-type). */
  shouldInitializePopperFocus?: boolean;
  config?: Partial<FocusTrapConfig>;
}

export interface UseDatePickerFocusTrapReturn {
  handlePopperKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  focusTrapProps: {
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
    tabIndex: -1;
    role: 'dialog';
    'aria-modal': boolean;
    'aria-label': string;
  };
  moveFocusToNextZone: () => void;
  moveFocusToPreviousZone: () => void;
  returnFocusToInput: (delay?: number) => void;
  currentFocusZone: FocusZoneType | null;
}

const DEFAULT_FOCUS_TRAP_CONFIG: FocusTrapConfig = {
  enabled: true,
  returnFocusOnClose: true,
  escapeKeyClosesPopper: true,
  tabCycleOrder: [
    'relative-dates',
    'previous-month-button',
    'month-select',
    'year-select',
    'next-month-button',
    'calendar',
    'close-button',
  ],
  initialFocusZone: 'relative-dates',
};

// Delay before focusing a control/close button, letting the tabindex re-render land first.
const ZONE_FOCUS_DELAY_MS = 10;

const calendarControlFocusSelectors: Partial<Record<FocusZoneType, string>> = {
  'previous-month-button': '[data-testid*="buttonPreviousMonth-iconButton"]',
  'month-select': '[data-testid*="choiceMonth-choiceInput"]',
  'year-select': '[data-testid*="choiceYear-choiceInput"]',
  'next-month-button': '[data-testid*="buttonNextMonth-iconButton"]',
};

export function useDatePickerFocusTrap(
  props: UseDatePickerFocusTrapProps
): UseDatePickerFocusTrapReturn {
  const config = {...DEFAULT_FOCUS_TRAP_CONFIG, ...props.config};
  const currentFocusZoneRef = useRef<FocusZoneType | null>(null);
  const previousFocusElementRef = useRef<HTMLElement | null>(null);

  const availableZones = config.tabCycleOrder.filter((zone) => {
    if (zone === 'relative-dates' && !props.hasRelativeDates) {
      return false;
    }
    return true;
  });

  const getCurrentFocusZone = useCallback((): FocusZoneType | null => {
    if (props.relativeDatesFocused && props.hasRelativeDates) {
      return 'relative-dates';
    }
    if (props.calendarFocusedDate) {
      return 'calendar';
    }
    return currentFocusZoneRef.current;
  }, [props.relativeDatesFocused, props.hasRelativeDates, props.calendarFocusedDate]);

  // These callbacks are intentionally stable because getFocusableElement feeds
  // moveFocusToZone, which is a dependency of initializeFocusZone.
  const getFocusableElement = useCallback(
    (targetZone: FocusZoneType) => {
      const selector =
        targetZone === 'close-button'
          ? '[data-testid*="closeButton"]'
          : getSafely(calendarControlFocusSelectors, targetZone);

      if (!selector) {
        return null;
      }

      const element = props.popperRef.current?.querySelector(selector);

      if (!(element instanceof HTMLElement)) {
        return null;
      }

      return element;
    },
    [props.popperRef]
  );

  const isZoneFocusable = useCallback(
    (targetZone: FocusZoneType) => {
      if (targetZone === 'relative-dates') {
        return props.hasRelativeDates;
      }

      if (targetZone === 'calendar') {
        return true;
      }

      const element = getFocusableElement(targetZone);

      if (!element) {
        return false;
      }

      if (element instanceof HTMLButtonElement && element.disabled) {
        return false;
      }

      return element.getAttribute('aria-disabled') !== 'true';
    },
    [getFocusableElement, props.hasRelativeDates]
  );

  const returnFocusToInput = useCallback(
    (delay = 0) => {
      if (props.inputRef.current && config.returnFocusOnClose) {
        if (delay > 0) {
          setTimeout(() => {
            props.inputRef.current?.focus();
          }, delay);
        } else {
          props.inputRef.current.focus();
        }
      }
    },
    [props.inputRef, config.returnFocusOnClose]
  );

  const {
    onRelativeDatesFocusChange,
    onCalendarFocusChange,
    defaultCalendarDate,
    hasRelativeDates,
    calendarFocusedDate,
  } = props;
  const moveFocusToZone = useCallback(
    (targetZone: FocusZoneType) => {
      currentFocusZoneRef.current = targetZone;

      switch (targetZone) {
        case 'relative-dates':
          if (hasRelativeDates) {
            onRelativeDatesFocusChange(true, 0);
            onCalendarFocusChange(null);
          }
          break;
        case 'calendar':
          onRelativeDatesFocusChange(false, -1);
          if (!calendarFocusedDate) {
            const initialDate = defaultCalendarDate ?? new Date();
            onCalendarFocusChange(initialDate);
          }
          break;
        case 'previous-month-button':
        case 'month-select':
        case 'year-select':
        case 'next-month-button':
          onRelativeDatesFocusChange(false, -1);
          onCalendarFocusChange(null);
          setTimeout(() => {
            getFocusableElement(targetZone)?.focus();
          }, ZONE_FOCUS_DELAY_MS);
          break;
        case 'close-button':
          onRelativeDatesFocusChange(false, -1);
          onCalendarFocusChange(null);
          setTimeout(() => {
            getFocusableElement(targetZone)?.focus();
          }, ZONE_FOCUS_DELAY_MS);
          break;
      }
    },
    [
      hasRelativeDates,
      onRelativeDatesFocusChange,
      onCalendarFocusChange,
      calendarFocusedDate,
      defaultCalendarDate,
      getFocusableElement,
    ]
  );

  const getNextFocusableZone = useCallback(
    (direction: 1 | -1) => {
      const currentZone = getCurrentFocusZone();
      const currentIndex = currentZone ? availableZones.indexOf(currentZone) : -1;
      const baseIndex = match<[number, 1 | -1], number>([currentIndex, direction])
        .with([-1, 1], () => -1)
        .with([-1, Pattern.any], () => 0)
        .otherwise(() => currentIndex);

      for (let offset = 1; offset <= availableZones.length; offset++) {
        const zoneIndex =
          (baseIndex + direction * offset + availableZones.length) % availableZones.length;
        const zone = getSafely(availableZones, zoneIndex);

        if (zone && isZoneFocusable(zone)) {
          return zone;
        }
      }

      return null;
    },
    [availableZones, getCurrentFocusZone, isZoneFocusable]
  );

  const moveFocusToNextZone = useCallback(() => {
    const nextZone = getNextFocusableZone(1);

    if (nextZone) {
      moveFocusToZone(nextZone);
    }
  }, [getNextFocusableZone, moveFocusToZone]);

  const moveFocusToPreviousZone = useCallback(() => {
    const prevZone = getNextFocusableZone(-1);

    if (prevZone) {
      moveFocusToZone(prevZone);
    }
  }, [getNextFocusableZone, moveFocusToZone]);

  const handleTabNavigation = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!config.enabled) {
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        if (event.shiftKey) {
          moveFocusToPreviousZone();
        } else {
          moveFocusToNextZone();
        }
      }
    },
    [config.enabled, moveFocusToPreviousZone, moveFocusToNextZone]
  );

  const {onClose} = props;
  const handleEscapeKey = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape' && config.escapeKeyClosesPopper) {
        event.preventDefault();
        event.stopPropagation(); // Prevent ESC from bubbling to parent modals
        onClose();
      }
    },
    [config.escapeKeyClosesPopper, onClose]
  );

  const handlePopperKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!config.enabled) {
        return;
      }

      // Only handle Escape and Tab keys in focus trap
      // Let other keys (arrows, Enter, Space, etc.) pass through to existing handlers
      if (event.key === 'Escape') {
        handleEscapeKey(event);
        return;
      }

      if (event.key === 'Tab') {
        handleTabNavigation(event);
        return;
      }

      // For all other keys, let the default behavior continue
      // This allows arrow keys, Enter, Space, etc. to work in calendar and relative dates
    },
    [config.enabled, handleEscapeKey, handleTabNavigation]
  );

  // Store previous focus element when popper opens
  useEffect(
    function storePreviousFocusElement() {
      if (props.isOpen && !previousFocusElementRef.current) {
        previousFocusElementRef.current = document.activeElement as HTMLElement;
      } else if (!props.isOpen) {
        previousFocusElementRef.current = null;
        currentFocusZoneRef.current = null;
      }
    },
    [props.isOpen]
  );

  // Initialize focus zone when popper opens (after paint so chip/day refs exist)
  useEffect(
    function initializeFocusZone() {
      if (
        !props.isOpen ||
        !config.enabled ||
        currentFocusZoneRef.current ||
        !props.shouldInitializePopperFocus
      ) {
        return;
      }

      let initialZone = availableZones[0];
      if (config.initialFocusZone && availableZones.includes(config.initialFocusZone)) {
        initialZone = config.initialFocusZone;
      }

      if (!initialZone) {
        return;
      }

      const frameId = requestAnimationFrame(() => {
        moveFocusToZone(initialZone);
      });

      return () => cancelAnimationFrame(frameId);
    },
    [
      props.isOpen,
      props.shouldInitializePopperFocus,
      config.enabled,
      config.initialFocusZone,
      availableZones,
      moveFocusToZone,
    ]
  );

  const focusTrapProps = {
    onKeyDown: handlePopperKeyDown,
    tabIndex: -1 as const,
    role: 'dialog' as const,
    'aria-modal': props.isOpen && config.enabled,
    'aria-label': 'Date picker',
  };

  return {
    handlePopperKeyDown,
    focusTrapProps,
    moveFocusToNextZone,
    moveFocusToPreviousZone,
    returnFocusToInput,
    currentFocusZone: getCurrentFocusZone(),
  };
}
